import * as React from "react";
import { cn } from "../lib/utils";
import { Badge } from "../primitives/badge";
import { handleKanbanKey, type KeyboardDndState } from "./kanban-keyboard-dnd";

/**
 * KanbanColumn describes a vertical column in the board (one per status).
 */
export interface KanbanColumn<T> {
  id: string;
  title: string;
  /** Optional icon shown next to the title (Lucide component). */
  icon?: React.ComponentType<{ className?: string }>;
  /** Optional accent color for the column header (Tailwind color name root, e.g., "blue"). */
  accent?: string;
  /** Items belonging to this column. */
  items: T[];
}

export interface KanbanBoardProps<T> {
  columns: KanbanColumn<T>[];
  /**
   * Render function for a single card. Receives the item and a handle to
   * trigger drag-start (passed back to onDragStart).
   */
  renderCard: (item: T) => React.ReactNode;
  /** Optional click handler invoked when a card is clicked. */
  onCardClick?: (item: T, columnId: string) => void;
  /** Drag-and-drop hook — fired when a card is dropped in a different column. */
  onDrop?: (item: T, fromColumnId: string, toColumnId: string) => void;
  /** Unique identifier extractor for items. */
  itemKey: (item: T) => string | number;
  /** Optional empty state per column. Defaults to a subtle "Nothing here" message. */
  emptyContent?: React.ReactNode;
  className?: string;
}

/**
 * Kanban board — Odoo-style multi-column view, with optional drag-and-drop.
 * Uses HTML5 drag events under the hood so it works without external DnD libs
 * and degrades gracefully on touch devices (cards remain clickable even if drag
 * doesn't trigger). Cards are also keyboard-operable: Space picks a card up
 * (Enter too when the board has no click action; with one, Enter activates
 * the card like any role="button"), arrow keys move it across columns
 * (Left/Right) or within one (Up/Down), Enter/Space drops, Escape cancels —
 * with aria-live announcements for screen readers. The keyboard route fires
 * the same onDrop tuple as a pointer drop, and after a drop or cancel focus
 * is restored to the card in its destination column.
 */
export function KanbanBoard<T>({
  columns,
  renderCard,
  onCardClick,
  onDrop,
  itemKey,
  emptyContent,
  className,
}: KanbanBoardProps<T>) {
  const [draggingItem, setDraggingItem] = React.useState<{ item: T; from: string } | null>(null);
  const [keyboardDnd, setKeyboardDnd] = React.useState<KeyboardDndState<T> | null>(null);
  const [announcement, setAnnouncement] = React.useState("");

  // Post-drop focus restore: card DOM nodes by item key, plus the key of the
  // card that should regain focus after a keyboard drop/cancel re-renders the
  // board (the dropped card moves to another column, so focus must follow it
  // rather than fall back to <body>).
  const cardRefs = React.useRef(new Map<string | number, HTMLDivElement>());
  const pendingFocusKey = React.useRef<string | number | null>(null);
  React.useEffect(() => {
    const key = pendingFocusKey.current;
    if (key === null) return;
    pendingFocusKey.current = null;
    cardRefs.current.get(key)?.focus();
  });

  const columnSummaries = columns.map((col) => ({
    id: col.id,
    title: col.title,
    itemCount: col.items.length,
  }));

  const handleDragStart = (item: T, columnId: string) => (e: React.DragEvent) => {
    setDraggingItem({ item, from: columnId });
    // Pointer drag supersedes any in-flight keyboard grab.
    setKeyboardDnd(null);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (toColumnId: string) => () => {
    if (draggingItem && draggingItem.from !== toColumnId) {
      onDrop?.(draggingItem.item, draggingItem.from, toColumnId);
    }
    setDraggingItem(null);
  };

  const handleCardKeyDown =
    (item: T, columnIndex: number, itemIndex: number) => (e: React.KeyboardEvent) => {
      if (!onDrop) {
        // No DnD on this board — Enter/Space still activates the click action.
        const column = columns[columnIndex];
        if ((e.key === "Enter" || e.key === " ") && onCardClick && column) {
          e.preventDefault();
          onCardClick(item, column.id);
        }
        return;
      }
      const action = handleKanbanKey(
        e.key,
        keyboardDnd,
        { item, columnIndex, itemIndex },
        columnSummaries,
        // With a click action on the board, idle Enter activates the card and
        // Space grabs — resolving the Enter-activation vs grab conflict.
        { enterActivates: !!onCardClick },
      );
      if (action.type === "ignored") return;
      e.preventDefault();
      e.stopPropagation();
      switch (action.type) {
        case "activated": {
          const column = columns[columnIndex];
          if (onCardClick && column) onCardClick(item, column.id);
          break;
        }
        case "grabbed":
        case "moved":
          setKeyboardDnd(action.state);
          setAnnouncement(action.announcement);
          break;
        case "dropped":
          // Same tuple the pointer path fires; null means same-column no-op.
          if (action.drop) {
            onDrop(action.drop.item, action.drop.fromColumnId, action.drop.toColumnId);
          }
          // Restore focus to the dropped card after it re-renders in its
          // destination column.
          if (keyboardDnd) pendingFocusKey.current = itemKey(keyboardDnd.item);
          setKeyboardDnd(null);
          setAnnouncement(action.announcement);
          break;
        case "cancelled":
          // The card never moved — re-assert focus on it for continuity.
          if (keyboardDnd) pendingFocusKey.current = itemKey(keyboardDnd.item);
          setKeyboardDnd(null);
          setAnnouncement(action.announcement);
          break;
      }
    };

  const cardsInteractive = !!onDrop || !!onCardClick;

  return (
    <div
      className={cn(
        "flex gap-4 overflow-x-auto pb-4 [&::-webkit-scrollbar]:h-2 [&::-webkit-scrollbar-thumb]:bg-muted-foreground/30",
        className,
      )}
    >
      <div aria-live="assertive" role="status" className="sr-only">
        {announcement}
      </div>
      {columns.map((col, columnIndex) => (
        <div
          key={col.id}
          className={cn(
            "flex w-80 shrink-0 flex-col rounded-lg border bg-muted/30",
            keyboardDnd &&
              keyboardDnd.columnIndex === columnIndex &&
              "ring-2 ring-ring/60",
          )}
          onDragOver={handleDragOver}
          onDrop={handleDrop(col.id)}
        >
          <div className="flex items-center justify-between border-b bg-background/60 px-3 py-2">
            <div className="flex items-center gap-2 text-sm font-medium">
              {col.icon ? <col.icon className="h-4 w-4 text-muted-foreground" /> : null}
              <span>{col.title}</span>
            </div>
            <Badge variant="secondary" className="text-xs">
              {col.items.length}
            </Badge>
          </div>

          <div className="flex flex-1 flex-col gap-2 p-2">
            {col.items.length === 0
              ? (emptyContent ?? <KanbanEmptyHint />)
              : col.items.map((item, itemIndex) => {
                  const grabbed =
                    keyboardDnd !== null && itemKey(keyboardDnd.item) === itemKey(item);
                  return (
                    <div
                      key={itemKey(item)}
                      ref={(node) => {
                        const key = itemKey(item);
                        if (node) cardRefs.current.set(key, node);
                        else cardRefs.current.delete(key);
                      }}
                      draggable={!!onDrop}
                      onDragStart={handleDragStart(item, col.id)}
                      onClick={() => onCardClick?.(item, col.id)}
                      tabIndex={cardsInteractive ? 0 : undefined}
                      role={cardsInteractive ? "button" : undefined}
                      aria-roledescription={
                        onDrop
                          ? onCardClick
                            ? "Draggable card. Press Space to pick up, arrow keys to move between and within columns, Enter or Space to drop, Escape to cancel. Press Enter to open."
                            : "Draggable card. Press Enter or Space to pick up, arrow keys to move between and within columns, Enter to drop, Escape to cancel."
                          : undefined
                      }
                      aria-pressed={onDrop ? grabbed : undefined}
                      onKeyDown={
                        cardsInteractive
                          ? handleCardKeyDown(item, columnIndex, itemIndex)
                          : undefined
                      }
                      className={cn(
                        "rounded-md border bg-background p-3 text-sm shadow-sm transition-all",
                        onCardClick && "cursor-pointer hover:shadow-md hover:-translate-y-0.5",
                        onDrop && "cursor-grab active:cursor-grabbing",
                        cardsInteractive &&
                          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                        grabbed && "opacity-70 ring-2 ring-ring",
                      )}
                    >
                      {renderCard(item)}
                    </div>
                  );
                })}
          </div>
        </div>
      ))}
    </div>
  );
}

function KanbanEmptyHint() {
  return (
    <div className="flex items-center justify-center rounded-md border border-dashed border-muted-foreground/20 px-3 py-6 text-xs text-muted-foreground">
      Nothing here yet
    </div>
  );
}
